/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Pure presentation helpers for the guest Track Order view. Kept free of React so the
 * mapping logic (which status groups to show, how to label/badge them, address and money
 * formatting) is unit-testable in isolation from the components.
 */
import type {
    GuestOrderReason,
    SomAddress,
    SomFulfillmentOrder,
    SomOrderItem,
    SomOrderModel,
} from '@/lib/order/guest-lookup.server';

/** The SFRA model's "unset" sentinel, used for both address blocks and money fields. */
const UNSET = '-';

/**
 * All fulfillment groups across the order, in the SFRA display order (Ordered → InProgress
 * → Shipped → Canceled → Returned). Each status array can hold more than one group, so the
 * arrays are flattened rather than indexed at `[0]`, which shows every group the order has
 * (the read-only view collapses SFRA's per-group drill-down into a single page).
 */
export function collectFulfillmentGroups(order: SomOrderModel): SomFulfillmentOrder[] {
    return [
        ...(order.orderedStatusGroupItems ?? []),
        ...(order.inProgressStatusGroupItems ?? []),
        ...(order.shippedStatusGroupItems ?? []),
        ...(order.canceledStatusGroupItems ?? []),
        ...(order.returnedStatusGroupItems ?? []),
    ].filter((group) => (group?.orderItems?.orderItems?.length ?? 0) > 0);
}

/** The `trackOrder` status label keys, kept as a literal union so `t()` accepts the result. */
export type StatusLabelKey =
    | 'status.ordered'
    | 'status.inProgress'
    | 'status.shipped'
    | 'status.delivered'
    | 'status.partiallyDelivered'
    | 'status.canceled'
    | 'status.returned';

/** i18n key (under the `trackOrder` namespace) for a group's status label. */
export function getStatusLabelKey(group: SomFulfillmentOrder): StatusLabelKey {
    switch (group.status) {
        case 'Ordered':
            return 'status.ordered';
        case 'InProgress':
            return 'status.inProgress';
        case 'Shipped':
            // A shipped group's real state is refined by the tracking roll-up.
            if (group.shipmentStatus === 'delivered') return 'status.delivered';
            if (group.shipmentStatus === 'partially_delivered') return 'status.partiallyDelivered';
            return 'status.shipped';
        case 'Canceled':
            return 'status.canceled';
        case 'Returned':
            return 'status.returned';
        default:
            return 'status.inProgress';
    }
}

/** Badge variant for a group's status: green for active/complete, red for canceled, neutral for returned. */
export function getStatusBadgeVariant(group: SomFulfillmentOrder): 'success' | 'destructive' | 'secondary' {
    if (group.status === 'Canceled') return 'destructive';
    if (group.status === 'Returned') return 'secondary';
    return 'success';
}

/**
 * Whether a pre-formatted money string represents a present, non-zero amount and is worth
 * rendering. Filters the SFRA `'-'` default and any all-zero value (e.g. `$0.00`, `0,00 €`)
 * regardless of locale grouping/decimal marks — a discount row of zero is noise. This is a
 * deliberate cleanup of SFRA's `productDiscount === 0` guard, which never matched because
 * the model stores a formatted string, so SFRA always rendered zero-discount rows.
 */
export function isNonZeroMoney(value: string | undefined | null): boolean {
    if (!value || value === UNSET) return false;
    // Any digit 1-9 anywhere means the amount is non-zero; a pure `$0.00` has none.
    return /[1-9]/.test(value);
}

/** A displayable money value: present and not the `'-'` sentinel (zero amounts still show). */
export function isPresentMoney(value: string | undefined | null): boolean {
    return !!value && value !== UNSET;
}

/** Parse the ISO `orderedDate` into locale-formatted date and time parts. Falls back to the raw string. */
export function formatOrderedDateTime(iso: string | undefined, locale: string): { date: string; time: string } {
    if (!iso) return { date: '', time: '' };
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
        return { date: iso, time: '' };
    }
    return {
        date: parsed.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' }),
        time: parsed.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' }),
    };
}

/** Type guard: an address block that carries real data (not the `'-'` sentinel or undefined). */
export function hasAddress(address: SomAddress | '-' | undefined | null): address is SomAddress {
    return !!address && address !== UNSET && typeof address === 'object';
}

/**
 * A single displayable field value, or `undefined` when it is missing / the `'-'` sentinel.
 * Accepts `number` too: the SOM model JSON-serializes some fields as numbers (notably payment
 * `ExpiryMonth`/`ExpiryYear`, e.g. `4`/`2031`), so coerce with `String()` before trimming
 * rather than assume a string — calling `.trim()` on a number throws at render time.
 */
export function presentField(value: string | number | undefined | null): string | undefined {
    if (value == null || value === UNSET) return undefined;
    const trimmed = String(value).trim();
    return trimmed && trimmed !== UNSET ? trimmed : undefined;
}

/**
 * Every order line across all fulfillment groups, deduplicated by `orderItemSummaryId` (a line
 * lives in exactly one status group, but dedup guards against any overlap so a shopper never
 * sees the same item twice in a cancel/return list). The availability counters are
 * OrderItemSummary-level, so the first occurrence carries authoritative values.
 */
export function flattenOrderItems(order: SomOrderModel): SomOrderItem[] {
    const seen = new Set<string>();
    const items: SomOrderItem[] = [];
    for (const group of collectFulfillmentGroups(order)) {
        for (const item of group.orderItems?.orderItems ?? []) {
            const id = item.orderItemSummaryId;
            if (id && !seen.has(id)) {
                seen.add(id);
                items.push(item);
            }
        }
    }
    return items;
}

/** Lines the shopper can still cancel (SOM reports remaining cancelable quantity). */
export function getCancelableItems(order: SomOrderModel): SomOrderItem[] {
    return flattenOrderItems(order).filter((item) => (item.quantityAvailableToCancel ?? 0) > 0);
}

/**
 * Lines the shopper can still return: SOM reports remaining returnable quantity AND the product
 * is not flagged non-returnable (`nonRMA`) — the cartridge rejects nonRMA items, so hiding them
 * keeps the dialog in step with what a submit would accept.
 */
export function getReturnableItems(order: SomOrderModel): SomOrderItem[] {
    return flattenOrderItems(order).filter((item) => (item.quantityAvailableToReturn ?? 0) > 0 && !item.nonRMA);
}

/** The max quantity a line can be canceled/returned in, per the SOM availability counter (min 1). */
export function maxCancelQuantity(item: SomOrderItem): number {
    const qty = item.quantityAvailableToCancel;
    return typeof qty === 'number' && qty > 0 ? qty : 1;
}

/** @see maxCancelQuantity */
export function maxReturnQuantity(item: SomOrderItem): number {
    const qty = item.quantityAvailableToReturn;
    return typeof qty === 'number' && qty > 0 ? qty : 1;
}

/**
 * A display label for a cancel/return reason. The SFRA cancel-reason list carries resource KEYS
 * as its `label` (e.g. `label.order.cancel.reason.WrongItem`) that do not resolve in the headless
 * storefront, so a dotted/`label.`-prefixed label falls back to the already-human-readable
 * `value`. A plain label (e.g. a merchant-authored return reason) is used as-is.
 */
export function reasonDisplayLabel(reason: GuestOrderReason): string {
    const { label, value } = reason;
    if (label && !label.includes('.')) {
        return label;
    }
    return value;
}

/**
 * The historical "Other" sentinel, kept as the fallback when the lookup meta doesn't carry a
 * value (older cartridge bundle). Prefer `meta.otherReasonValue` — the value the cartridge
 * actually validates against — so the client's free-text gate can never drift from the server.
 */
export const OTHER_REASON_VALUE = 'Other';

/**
 * Whether a chosen reason is the "Other" option (so the UI must collect free-text detail).
 * Compares (case-insensitively) against the cartridge-supplied `otherReasonValue`, defaulting
 * to {@link OTHER_REASON_VALUE} when a caller has none to pass.
 */
export function isOtherReason(
    reasonValue: string | undefined | null,
    otherReasonValue: string = OTHER_REASON_VALUE
): boolean {
    return !!reasonValue && reasonValue.trim().toLowerCase() === otherReasonValue.trim().toLowerCase();
}

/**
 * A concise one-line label for an order item in a cancel/return list: product name plus any
 * variation attributes, e.g. `Tote (Color: Black, Size: M)`. Text-only, no thumbnail.
 */
export function itemDisplayLine(item: SomOrderItem): string {
    const name = item.productName ?? '';
    const variations = (item.variationAttributes ?? [])
        .map((attribute) => `${attribute.displayName}: ${attribute.displayValue}`)
        .join(', ');
    return variations ? `${name} (${variations})` : name;
}

/** Compose the "City, State PostalCode" line from an address, skipping missing parts. */
export function cityStatePostal(address: SomAddress): string {
    const city = presentField(address.city);
    const state = presentField(address.state);
    const postalCode = presentField(address.postalCode);
    const region = [state, postalCode].filter(Boolean).join(' ');
    return [city, region].filter(Boolean).join(', ');
}
